/**
 * Stripe webhook inbox (BILL-17): types and pure mapping for the platform
 * admin card. The backend (`billing_operations.py`) returns metadata only;
 * the mapping keeps it that way and never carries payload fields through.
 */
import { parseServerDate } from './serverDate';

export const WEBHOOK_EVENT_STATUSES = [
  'pending',
  'processing',
  'failed',
  'dead_letter',
  'succeeded',
  'ignored',
] as const;

export type WebhookEventStatus = (typeof WEBHOOK_EVENT_STATUSES)[number];

/** Statuses an operator should look at: not finished, or finished badly. */
export const ATTENTION_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'processing',
  'failed',
  'dead_letter',
]);

/**
 * No retry action for these. The backend 409s on succeeded/ignored unless
 * `force=true` (the card does not offer a forced replay). A `processing` row
 * holds a worker lease; retrying it would reset the lease and could process the
 * event twice. The stale-lease reaper in stripe_tasks.py requeues stuck rows.
 */
const NON_RETRYABLE_STATUSES: ReadonlySet<string> = new Set(['succeeded', 'ignored', 'processing']);

export type InboxFilter = 'attention' | 'all';

export interface WebhookEventSummary {
  stripe_event_id: string;
  event_type: string;
  stripe_object_id?: string | null;
  status: string;
  attempts: number;
  duplicate_count?: number | null;
  received_at?: string | null;
  processed_at?: string | null;
  next_attempt_at?: string | null;
  last_error?: string | null;
}

export interface WebhookEventsResponse {
  events?: WebhookEventSummary[] | null;
  count?: number;
}

export interface InboxRow {
  id: string;
  eventType: string;
  status: string;
  statusTone: 'neutral' | 'primary' | 'success' | 'warning' | 'error';
  attempts: number;
  receivedAt: Date | null;
  nextAttemptAt: Date | null;
  errorSnippet: string | null;
  canRetry: boolean;
}

export const ERROR_SNIPPET_MAX = 120;

function statusTone(status: string): InboxRow['statusTone'] {
  switch (status) {
    case 'failed':
    case 'dead_letter':
      return 'error';
    case 'pending':
    case 'processing':
      return 'warning';
    case 'succeeded':
      return 'success';
    default:
      return 'neutral';
  }
}

function snippet(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  if (!text) return null;
  return text.length > ERROR_SNIPPET_MAX ? `${text.slice(0, ERROR_SNIPPET_MAX)}…` : text;
}

/** Map the list response to display rows, newest first, filtered for the selected view. */
export function mapInboxEvents(
  response: WebhookEventsResponse | null | undefined,
  filter: InboxFilter = 'attention',
): InboxRow[] {
  const events = Array.isArray(response?.events) ? response!.events! : [];
  return events
    .filter((event) => event && typeof event.stripe_event_id === 'string' && event.stripe_event_id)
    .map((event) => {
      const status = String(event.status || '').toLowerCase();
      return {
        id: event.stripe_event_id,
        eventType: event.event_type || '',
        status,
        statusTone: statusTone(status),
        attempts: Number.isFinite(event.attempts) ? Number(event.attempts) : 0,
        receivedAt: parseServerDate(event.received_at ?? null),
        nextAttemptAt: parseServerDate(event.next_attempt_at ?? null),
        errorSnippet: snippet(event.last_error),
        canRetry: !NON_RETRYABLE_STATUSES.has(status),
      };
    })
    .filter((row) => filter === 'all' || ATTENTION_STATUSES.has(row.status))
    .sort((a, b) => (b.receivedAt?.getTime() ?? 0) - (a.receivedAt?.getTime() ?? 0));
}

/** i18n key for a status label; unknown statuses fall back to the raw value at the call site. */
export const WEBHOOK_STATUS_LABEL_KEYS: Record<WebhookEventStatus, string> = {
  pending: 'platformAdmin.webhookInbox.statusPending',
  processing: 'platformAdmin.webhookInbox.statusProcessing',
  failed: 'platformAdmin.webhookInbox.statusFailed',
  dead_letter: 'platformAdmin.webhookInbox.statusDeadLetter',
  succeeded: 'platformAdmin.webhookInbox.statusSucceeded',
  ignored: 'platformAdmin.webhookInbox.statusIgnored',
};

export function webhookStatusLabelKey(status: string): string | null {
  return (WEBHOOK_STATUS_LABEL_KEYS as Record<string, string>)[status] ?? null;
}
